import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { login, getProfile } from '../api/auth';
import { APP_ROUTES } from '../routes';
import { Eye, EyeOff } from 'lucide-react';

const Login: React.FC = () => {
    const { state: authState, dispatch } = useAuth();
    const navigate = useNavigate();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // If user is already authenticated, redirect to the dashboard
        if (authState.isAuthenticated) {
            navigate(APP_ROUTES.DASHBOARD);
        }
    }, [authState.isAuthenticated, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        try {
            const { access_token, refresh_token } = await login({ email, password });

            localStorage.setItem('accessToken', access_token);
            localStorage.setItem('refreshToken', refresh_token);

            dispatch({
                type: 'LOGIN_SUCCESS',
                payload: { accessToken: access_token, refreshToken: refresh_token },
            });

            try {
                const user = await getProfile();
                dispatch({ type: 'SET_USER', payload: { user } });
            } catch (profileError) {
                console.warn("Could not fetch full user profile after login, proceeding with basic info from token.", profileError);
            }

            navigate(APP_ROUTES.DASHBOARD);

        } catch (err) {
            setError('Failed to login. Please check your credentials and try again.');
            console.error('Login failed:', err);
        }
    };

    // Render nothing or a loader while checking auth state
    if (authState.isLoading || authState.isAuthenticated) {
        return null; // or a loading spinner
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-md">
                <div className="flex justify-center">
                    <img src="/favicon.svg" alt="Logo" className="w-auto h-12" />
                </div>
                <h2 className="mt-6 text-2xl font-bold text-center text-gray-900">Login to your account</h2>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label htmlFor="email-address" className="sr-only">Email address</label>
                            <input
                                id="email-address"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                className="relative block w-full px-3 py-2 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-none appearance-none rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                placeholder="Email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div className="relative">
                            <label htmlFor="password" className="sr-only">Password</label>
                            <input
                                id="password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="current-password"
                                required
                                className="relative block w-full px-3 pr-10 py-2 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-none appearance-none rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <div className="absolute inset-y-0 right-0 z-20 flex items-center pr-3">
                                {showPassword ? (
                                    <EyeOff
                                        className="w-5 h-5 text-gray-400 cursor-pointer"
                                        onClick={() => setShowPassword(false)}
                                    />
                                ) : (
                                    <Eye
                                        className="w-5 h-5 text-gray-400 cursor-pointer"
                                        onClick={() => setShowPassword(true)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            className="relative flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md group hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Sign in
                        </button>
                    </div>
                    {error && <p className="mt-2 text-sm text-center text-red-600">{error}</p>}
                </form>
            </div>
        </div>
    );
};

export default Login;
